import { PolylineEdge, PolylineEdgeModel, h } from '@logicflow/core';
import { getThemeStyle } from '../theme';

/**
 * SequenceFlow — BPMN 2.0 solid arrow connecting elements within a process.
 *
 * Supports two visual markers at the start of the edge:
 * - Default flow: tick mark (/) when properties.isDefault === true
 * - Conditional flow: diamond when properties.conditionExpression is set
 *   (and isDefault is not true)
 */

class SequenceFlowView extends PolylineEdge {
  /**
   * Render a start-arrow marker based on flow properties:
   * - Default flow → diagonal tick mark
   * - Conditional flow → small diamond
   */
  getStartArrow() {
    const { model } = this.props;
    const { properties = {} } = model;
    const { stroke } = model.getEdgeStyle();
    const color = stroke || '#555';

    if (properties.isDefault === true || properties.isDefault === 'true') {
      // Default flow marker: diagonal tick mark (/)
      return h('path', {
        d: 'M -5 5 L 5 -5',
        stroke: color,
        strokeWidth: 2,
        fill: 'none',
      });
    }

    if (properties.conditionExpression && !properties.isDefault) {
      // Conditional flow marker: small diamond
      return h('path', {
        d: 'M 0 -5 L 5 0 L 0 5 L -5 0 Z',
        fill: '#fff',
        stroke: color,
        strokeWidth: 1.5,
      });
    }

    return null;
  }
}

class SequenceFlowModel extends PolylineEdgeModel {
  initEdgeData(data) {
    super.initEdgeData(data);
    this.text = { ...this.text, draggable: true };
  }

  getEdgeStyle() {
    const style = super.getEdgeStyle();
    const t = getThemeStyle(this.graphModel, 'sequenceFlow');
    style.stroke = t.stroke || '#555';
    style.strokeWidth = t.strokeWidth ?? 1.5;
    return style;
  }

  getTextStyle() {
    const style = super.getTextStyle();
    const t = getThemeStyle(this.graphModel, 'text');
    const canvas = getThemeStyle(this.graphModel, 'canvas');
    if (t && t.fill) style.color = t.fill;
    // Use the canvas background colour so the label "cuts through" arrows and
    // other edges behind it — correct for every theme (light, dark, custom).
    const bg = canvas.background || '#fff';
    if (style.background) style.background = { fill: bg };
    return style;
  }

  getArrow() {
    return {
      markerEnd: 'M -10 -5 L 0 0 L -10 5 z',
      refX: 8,
      refY: 0,
    };
  }

  // Place the label at the midpoint of the longest segment, offset
  // perpendicular to the segment direction — matching bpmn-js behaviour.
  // Works for straight edges, L-shapes and Z-shapes from ELK auto-layout.
  getTextPosition() {
    const pts = this.pointsList;
    if (!pts || pts.length < 2) {
      const pos = super.getTextPosition();
      return (isNaN(pos.x) || isNaN(pos.y)) ? { x: 0, y: 0 } : { x: pos.x, y: pos.y - 12 };
    }

    // Find the longest segment
    let best = 0, bestLen = 0;
    for (let i = 0; i < pts.length - 1; i++) {
      const dx = pts[i + 1].x - pts[i].x;
      const dy = pts[i + 1].y - pts[i].y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > bestLen) { bestLen = len; best = i; }
    }

    const a = pts[best], b = pts[best + 1];
    const mx = (a.x + b.x) / 2;
    const my = (a.y + b.y) / 2;

    // Always offset upward (-Y) so labels are readable above the arrow
    // regardless of segment direction (horizontal, vertical, or diagonal).
    return { x: mx, y: my - 12 };
  }
}

export const SequenceFlow = {
  type: 'epx:SequenceFlow',
  view: SequenceFlowView,
  model: SequenceFlowModel,
};
